import { useEffect, useState } from "react";
import axios from "axios";
import FormInput from "../../../components/input/FormInput";
import "./KVSTop.css";
import { useNavigate } from "react-router-dom";
import { base_url } from "../../../components/apiCall/webserviceurl";

const initialState = {
  groupParticipantNames: [""],
  groupNo: "",
  problemStatement: "",
  formatType: "",
  fullName: "",
  presentationFile: null,
};

const groupNumbers = Array.from({ length: 17 }, (_, i) => String(i));

const formatTypes = [
  "Literature search",
  "Algorithm development",
  "Design Synthesis",
];

const formatTypeToApi = {
  "Literature search": "literature_survey",
  "Algorithm development": "algorithm_development",
  "Design Synthesis": "design_synthesis",
};

const apiToFormatType = {
  literature_survey: "Literature search",
  algorithm_development: "Algorithm development",
  design_synthesis: "Design Synthesis",
};

function validate(fields) {
  const errors = {};

  if (!fields.problemStatement.trim())
    errors.problemStatement = "Problem statement is required.";

  if (!fields.formatType) errors.formatType = "Please select a type of format.";

  if (!fields.fullName.trim()) errors.fullName = "Full name is required.";

  if (!fields.presentationFile)
    errors.presentationFile = "Please upload your presentation.";

  return errors;
}

export default function Training_observation_plan_Top() {
  const [fields, setFields] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [presentationUrl, setPresentationUrl] = useState("");
  const localName = localStorage.getItem("name");

  const set = (key) => (value) => {
    if (hasExistingData && !isEditing) return;
    setFields((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  useEffect(() => {
    setFields((f) => ({ ...f, fullName: localName }));
  }, [localName]);

  // --- Fetch any existing submission and prefill / show as submitted ---
  useEffect(() => {
    const fetchExisting = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const res = await axios.get(
          `${base_url}/get-top-submission`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = res?.data?.data;
        if (data) {
          setFields((f) => ({
            ...f,
            problemStatement: data.problem_stmt || "",
            formatType: apiToFormatType[data.format_type] || "",
          }));

          if (!!data?.problem_stmt) setHasExistingData(true);

          const hasFile =
            data.ppt_file_path &&
            data.ppt_file_path !== "null" &&
            data.ppt_file_path.trim() !== "";

          if (hasFile) {
            setSubmitted(true);
            setPresentationUrl(`${base_url}${data.ppt_file_path}`);
          }
        }
      } catch (err) {
        console.log("No existing submission found:", err?.response?.data || err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExisting();
  }, []);

  // --- Group participant names: add/edit/remove ---
  const setParticipantName = (index) => (value) => {
    if (hasExistingData && !isEditing) return;
    setFields((f) => {
      const next = [...f.groupParticipantNames];
      next[index] = value;
      return { ...f, groupParticipantNames: next };
    });
    setErrors((e) => ({ ...e, groupParticipantNames: undefined }));
  };

  const addParticipant = () => {
    if (hasExistingData && !isEditing) return;
    setFields((f) => ({
      ...f,
      groupParticipantNames: [...f.groupParticipantNames, ""],
    }));
  };

  const removeParticipant = (index) => {
    if (hasExistingData && !isEditing) return;
    setFields((f) => {
      const next = f.groupParticipantNames.filter((_, i) => i !== index);
      return {
        ...f,
        groupParticipantNames: next.length ? next : [""],
      };
    });
  };

  const handleSubmit = async () => {
    if (hasExistingData && !isEditing) return;

    const errs = validate(fields);

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(firstKey);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitError("");
    setIsSubmitting(true);

    try {
      const gorup_id = localStorage.getItem("group_id");
      const formData = new FormData();
      formData.append("group_id", gorup_id);
      formData.append("problem_stmt", fields.problemStatement);
      formData.append(
        "format_type",
        formatTypeToApi[fields.formatType] || "design_synthesis",
      );
      formData.append("presentation", fields?.presentationFile || null);
      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${base_url}/top-submission`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const returnedPath = res?.data?.data?.ppt_file_path;
      if (returnedPath) {
        setPresentationUrl(`${base_url}${returnedPath}`);
      } else if (fields.presentationFile) {
        setPresentationUrl(URL.createObjectURL(fields.presentationFile));
      }

      setSubmitted(true);
      setHasExistingData(true);
      setIsEditing(false);
      setShowSuccessPopup(true);
    } catch (err) {
      setSubmitError(
        err.response?.data?.message ||
          "Something went wrong while submitting. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigate = useNavigate();

  const handleClear = () => {
    if (hasExistingData && !isEditing) return;
    setFields(initialState);
    setErrors({});
    setSubmitted(false);
    setSubmitError("");
    setIsEditing(false);
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="kvs-page">
        <p className="kvs-copy-note">Loading...</p>
      </div>
    );
  }

  return (
    <div className="kvs-page">
      <div className="kvs-card-grid">
        <div className="kvs-card">
          <FormInput
            id="problemStatement"
            type="text"
            label="Problem statement in one line"
            required
            placeholder="Your answer"
            value={fields.problemStatement}
            onChange={set("problemStatement")}
            error={errors.problemStatement}
            disabled={hasExistingData && !isEditing}
            readOnly={hasExistingData && !isEditing}
          />
        </div>

        <div className="kvs-card">
          <FormInput
            id="formatType"
            type="select"
            label="Type of format"
            required
            placeholder="Your answer"
            options={formatTypes}
            value={fields.formatType}
            onChange={set("formatType")}
            error={errors.formatType}
            disabled={hasExistingData && !isEditing}
          />
        </div>

        <div className="kvs-card">
          <FormInput
            id="fullName"
            type="text"
            label="Full name as per Service record"
            required
            placeholder="Your answer"
            value={fields.fullName}
            onChange={set("fullName")}
            error={errors.fullName}
            disabled={hasExistingData && !isEditing}
            readOnly={hasExistingData && !isEditing}
          />
        </div>

        <div className="kvs-card">
          <FormInput
            id="presentationFile"
            type="image"
            label="Upload Presentation"
            required
            placeholder="Upload 1 supported file. Max 10 MB."
            value={fields.presentationFile}
            onChange={set("presentationFile")}
            error={errors.presentationFile}
            disabled={hasExistingData && !isEditing}
          />

          {presentationUrl && (
            <a
              className="kvs-view-presentation-link"
              href={presentationUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
               View uploaded presentation
            </a>
          )}
        </div>
      </div>

      <p className="kvs-copy-note">
        A copy of your responses will be emailed to support@ihfc.co.in.
      </p>
      {submitError && <p className="kvs-field-error">{submitError}</p>}

      {hasExistingData && !isEditing && (
        <div className="kvs-submit-row">
          <button
            className="kvs-submit-btn"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>
        </div>
      )}

      {(!hasExistingData || isEditing) && (
        <div className="kvs-submit-row">
          <button
            className="kvs-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
          {/* <button
            className="kvs-clear-btn kvs-clear-btn--outline"
            onClick={handleClear}
          >
            Clear form
          </button> */}
        </div>
      )}

      {(!hasExistingData || isEditing) && (
        <p className="kvs-footer">Never submit passwords through Google Forms.</p>
      )}

      {/* Success popup */}
      {showSuccessPopup && (
        <div
          className="kvs-modal-backdrop"
          onClick={() => setShowSuccessPopup(false)}
        >
          <div
            className="kvs-success-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="kvs-modal-close-btn"
              onClick={() => setShowSuccessPopup(false)}
              aria-label="Close"
            >
              ✕
            </button>

            <div className="kvs-success-icon">✓</div>
            <h2 className="kvs-success-title">Submission Received!</h2>
            <p className="kvs-success-text">
              A copy of your responses will be emailed to support@ihfc.co.in.
            </p>

            {presentationUrl && (
              <a
                className="kvs-submit-btn"
                href={presentationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Presentation
              </a>
            )}

            <button
              className="kvs-clear-btn kvs-clear-btn--outline"
              onClick={() => setShowSuccessPopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <style>
        {`
        .kvs-view-presentation-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 10px;
          font-size: 14px;
          font-weight: 500;
          color: #2563eb;
          text-decoration: none;
          transition: color 0.15s ease;
        }

        .kvs-view-presentation-link:hover {
          color: #1d4ed8;
          text-decoration: underline;
        }

        .kvs-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(2px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: kvs-fade-in 0.15s ease-out;
        }

        .kvs-success-card {
          position: relative;
          background: #ffffff;
          border-radius: 16px;
          padding: 36px 32px 28px;
          max-width: 420px;
          width: 90%;
          text-align: center;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
          animation: kvs-scale-in 0.18s ease-out;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .kvs-modal-close-btn {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #f1f5f9;
          border: none;
          font-size: 15px;
          color: #475569;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s ease;
        }

        .kvs-modal-close-btn:hover {
          background: #e2e8f0;
        }

        .kvs-success-icon {
          width: 56px;
          height: 56px;
          margin: 4px auto 6px;
          border-radius: 50%;
          background: #dcfce7;
          color: #16a34a;
          font-size: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .kvs-success-title {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .kvs-success-text {
          font-size: 14px;
          color: #64748b;
          margin: 0 0 8px;
          line-height: 1.5;
        }

        .kvs-success-card .kvs-submit-btn {
          text-decoration: none;
          display: inline-block;
        }

        .kvs-success-card .kvs-clear-btn {
          margin-top: 4px;
        }

        @keyframes kvs-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes kvs-scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        `}
      </style>
    </div>
  );
}